import { CLOISTER_FLOOR } from '../../../site/cloisterLevels';
import type { Ring, Vec2 } from '../../../geo/types';
import type { MonasteryChurch, MonasteryCloister } from './monastery';
import type { PlanRect } from './genericBlock';

export const CLOISTER_WALK = {
  width: 1.8,
  wall: 0.35,
  door: 2.4,
  floor: CLOISTER_FLOOR.walk,
} as const;

export interface CloisterRange {
  readonly rect: PlanRect;
  readonly front: readonly [Vec2, Vec2];
  readonly ridgeAlongU: boolean;
  readonly entrance: boolean;
}

export function rectRing(r: PlanRect): Ring {
  return [
    [r.u0, r.v0],
    [r.u1, r.v0],
    [r.u1, r.v1],
    [r.u0, r.v1],
  ];
}

/** The entrance goes on the side opposite the chapel, leaving its adjoining walk intact. */
export function cloisterRanges(
  c: MonasteryCloister,
  church: MonasteryChurch | null
): CloisterRange[] {
  const g = c.garth / 2,
    d = c.range;
  const u0 = c.u - g,
    u1 = c.u + g,
    v0 = c.v - g,
    v1 = c.v + g;
  const southEntrance = church === null || church.v >= c.v;
  return [
    {
      rect: { u0: u0 - d, u1: u1 + d, v0: v1, v1: v1 + d },
      front: [
        [u0 - d, v1],
        [u1 + d, v1],
      ],
      ridgeAlongU: true,
      entrance: !southEntrance,
    },
    {
      rect: { u0: u0 - d, u1: u1 + d, v0: v0 - d, v1: v0 },
      front: [
        [u1 + d, v0],
        [u0 - d, v0],
      ],
      ridgeAlongU: true,
      entrance: southEntrance,
    },
    {
      rect: { u0: u1, u1: u1 + d, v0, v1 },
      front: [
        [u1, v1],
        [u1, v0],
      ],
      ridgeAlongU: false,
      entrance: false,
    },
    {
      rect: { u0: u0 - d, u1: u0, v0, v1 },
      front: [
        [u0, v0],
        [u0, v1],
      ],
      ridgeAlongU: false,
      entrance: false,
    },
  ];
}

export function rearRooms(range: CloisterRange): PlanRect[] {
  const r = range.rect,
    w = Math.min(CLOISTER_WALK.width, (range.ridgeAlongU ? r.v1 - r.v0 : r.u1 - r.u0) / 2);
  const a = range.front[0],
    b = range.front[1];
  const rear = range.ridgeAlongU
    ? { ...r, ...(b[0] > a[0] ? { v0: r.v0 + w } : { v1: r.v1 - w }) }
    : { ...r, ...(b[1] > a[1] ? { u1: r.u1 - w } : { u0: r.u0 + w }) };
  if (!range.entrance) return [rear];
  const centre = (r.u0 + r.u1) / 2,
    half = CLOISTER_WALK.door / 2;
  return [
    { ...rear, u1: centre - half },
    { ...rear, u0: centre + half },
  ];
}
